import { TimePickerChangeEvent } from '@progress/kendo-react-dateinputs'
import DateInput from 'components/DateInput'
import TimeInput from 'components/TimeInput'
import Translation from 'components/Translation'
import { useTenantContext } from 'context/TenantContext'
import { useCallback, useState } from 'react'
import { CalendarSlotItem } from 'types/calendar'
import { LocationItem, LocationSpace } from 'types/location'
import { ServiceApiTypeEnum } from 'types/service'
import { classNames } from 'utils/dom-helpers'

export type EditItemProps = {
    disable_date?: boolean
    item: CalendarSlotItem
    is_updating: boolean
    is_save_disabled: boolean
    onChange(key: string, value: string | number | Date): void
    onCancel(): void
    onSave(): void
}
export function GEditItem(p: EditItemProps) {
    const { item } = p
    const { staff, locations, services } = useTenantContext()
    const [disable_save, setDisableSave] = useState<boolean>(true)

    function handleDateChange(date: Date) {
        p.item.start = date
        p.onChange('start', date)
        setDisableSave(false)
    }

    const DatePicker = useCallback(() => {
        return (
            <DateInput
                key="edit_date"
                disabled={p?.disable_date}
                minDate={new Date()}
                className={classNames(
                    'focus:ring-primary-light focus:border-primary-light  border-gray-300 rounded-md calendar text-sm',
                    p?.disable_date
                        ? 'bg-gray-200 cursor-not-allowed'
                        : ''
                )}
                onChange={handleDateChange}
                dateFormat="dd/MM/yy"
                selected={p.item.start}
            />
        )
    }, [p.item.start])

    function handleTimeChange(e: TimePickerChangeEvent) {
        if (e.target.value) {
            const val: Date = e.target.value
            p.item.startTime = val
            p.onChange('startTime', val)
        } else {
            p.onChange('startTime', '')
        }
        setDisableSave(false)
    }

    const TimePicker = useCallback(() => {
        return (
            <TimeInput
                key="edit_time"
                onChange={handleTimeChange}
                defaultValue={
                    p.item.startTime
                        ? (p.item.startTime as Date)
                        : p.item.start
                            ? (p.item.start as Date)
                            : new Date()
                }
            />
        )
    }, [p.item.startTime])

    return (
        <div className="bg-primary-100 px-6 py-4">
            <div className="flex flex-col bg-gray-100 p-2 rounded-lg gap-4">
                <div className="flex items-center gap-4 field-item">
                    <label className="w-1/2" htmlFor="start">
                        <div className="editfields">
                        <Translation content_key="date" className="form-label" render_as="span"/>
                        <DatePicker />
                        </div>
                    </label>
                    <label className="w-1/2 " htmlFor="time">
                        <div className="editfields timefields">
                        <Translation
                            content_key="time"

                            render_as="span"
                            className="form-label"
                        />
                     
                            <TimePicker />
                        </div>
                        
                    </label>
                </div>

                <div className="flex items-center gap-4 field-item">
                    <label htmlFor="location" className="w-1/2">
                       <div className="editfields">
                       <Translation content_key="location" className="form-label" render_as="span" />
                        <select
                            id="location"
                            name="location"
                            className="focus:ring-primary-light focus:border-primary-light  border-gray-300 rounded-md text-sm w-full"
                            value={p.item?.location_id}
                            onChange={(e) => {
                                p.onChange('location_id', e.target.value)
                                setDisableSave(false)
                            }}
                        >
                            {locations ? (
                                locations.map((location: LocationItem) => (
                                    <option
                                        key={location.id}
                                        value={location.id}
                                    >
                                        {location.name}
                                    </option>
                                ))
                            ) : (
                                <Translation content_key="no_location_available" />
                            )}
                        </select>
                       </div>
                    </label>
                    <label htmlFor="location_space" className="w-1/2">
                        <div className="editfields">
                        <Translation content_key="location_space" className="form-label" render_as="span" />
                        <select
                            id="location_space"
                            name="location_space"
                            className="focus:ring-primary-light focus:border-primary-light  border-gray-300 rounded-md text-sm w-full"
                            value={p.item?.location_space_id}
                            onChange={(e) => {
                                p.onChange(
                                    'location_space_id',
                                    e.target.value
                                )
                                setDisableSave(false)
                            }}
                        >
                            {p.item?.location_id && locations ? (
                                locations
                                    .find(
                                        (location: LocationItem) =>
                                            location.id ===
                                            p.item?.location_id
                                    )
                                    ?.location_spaces?.map(
                                        (location_space: LocationSpace) => (
                                            <option
                                                key={location_space.id}
                                                value={location_space.id}
                                            >
                                                {location_space.name}
                                            </option>
                                        )
                                    )
                            ) : (
                                <Translation content_key="no_location_space_available" />
                            )}
                        </select>
                        </div>
                    </label>
                </div>

                <div className="flex items-center gap-4 field-item">
                    <div className="w-1/2 editfields">
                        <Translation content_key="staff" className="form-label" render_as="span" />

                        <select
                            id="staff"
                            name="staff"
                            className="focus:ring-primary-light focus:border-primary-light  border-gray-300 rounded-md text-sm w-full"
                            defaultValue={p.item.staff_id as string}
                            onChange={(e) => {
                                p.onChange('staff', e.target.value)
                                setDisableSave(false)
                            }}
                        >
                            {staff ? (
                                staff.map((staff) => (
                                    <option key={staff.id} value={staff.id}>
                                        {staff?.user?.first_name} &nbsp;
                                        {staff?.user?.last_name}
                                    </option>
                                ))
                            ) : (
                                <Translation content_key="no_staff_available" />
                            )}
                        </select>
                    </div>
                    {item?.appointment_id && (
                        <div className="w-1/2 editfields">
                            <Translation content_key="service" className="form-label" render_as="span" />
                            <select
                                id="staff"
                                name="service_id"
                                className="focus:ring-primary-light focus:border-primary-light  border-gray-300 rounded-md text-sm w-full"
                                defaultValue={p.item.service_id as string}
                                onChange={(e) => {
                                    p.onChange('service_id', e.target.value)
                                    setDisableSave(false)
                                }}
                            >
                                {services ? (
                                    services
                                        ?.filter(
                                            (item) =>
                                                item.service_type ===
                                                ServiceApiTypeEnum.APPOINTMENT
                                        )
                                        .map((item) => (
                                            <option
                                                key={item.id}
                                                value={item.id}
                                            >
                                                {item?.name}
                                            </option>
                                        ))
                                ) : (
                                    <Translation content_key="no_service_available" />
                                )}
                            </select>
                        </div>
                    )}
                </div> 

                <div className="flex gap-3 items-center mt-6 editfield-btns">
                    <button
                        type="button"
                        className="bg-gray-200 w-1/2 px-3 h-12 rounded-lg text-gray-600 text-center"
                        onClick={p.onCancel}
                    >
                        <Translation
                            render_as="span"
                            content_key="cancel"
                        />
                    </button>
                    <button
                        type="button"
                        className={`bg-${p.is_updating ||
                            disable_save ||
                            p.is_save_disabled
                            ? 'primary-lighter bg-opacity-30 cursor-not-allowed'
                            : 'primary'
                            } w-1/2 px-3 h-12 rounded-lg text-white text-center`}
                        onClick={p.onSave}
                        disabled={p.is_save_disabled || disable_save}
                    >
                        <Translation render_as="span" content_key="save" />
                    </button>
                </div>
            </div>
        </div>
    )
}
